import { Location } from 'containers/Organizations/components/Details/services/organizationDetails-types'
import cloneDeep from 'lodash/cloneDeep'
import filter from 'lodash/filter'
import forEach from 'lodash/forEach'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import sortedIndexBy from 'lodash/sortedIndexBy'

/** Scroll API may omit subclass fields or use snake_case; coerce so the tree can show property emails. */
export const normalizeLocationFromApi = (
  loc: Record<string, unknown>
): Location => {
  const next = { ...loc } as unknown as Location
  if (loc.locationType !== 'property') {
    return next
  }
  const raw = loc.emailAddresses ?? loc.email_addresses
  let emails: string[] = []
  if (Array.isArray(raw)) {
    emails = raw.map((e) => (e == null ? '' : String(e).trim())).filter(Boolean)
  } else if (raw != null && String(raw).trim() !== '') {
    emails = [String(raw).trim()]
  }
  next.emailAddresses = emails
  return next
}

const prepareProperties = (params: { nodes: Location[] }) => {
  const { nodes } = params
  const properties = map(nodes, (item) => ({
    ...normalizeLocationFromApi(item as Record<string, unknown>),
    expanded: false,
  }))

  return properties
}

const changeExpanded = (
  parentId: string,
  newList: Location[],
  locations: Location[]
) => {
  const tree = [...locations]
  let found = false

  const traverse = function (current: Location[]) {
    forEach(current, (item, key) => {
      if (!found) {
        if (item.id === parentId) {
          item.expanded = !item.expanded

          if (!item.children?.length) {
            item.children = map(newList, (child) =>
              normalizeLocationFromApi(child as Record<string, unknown>)
            )
            item.hasChild = true
          }

          found = true
        }

        if (item.children?.length) {
          traverse(item.children)
        }
      }
    })
  }

  traverse(tree)

  return tree
}

const shrinkNode = (id: string, locations: Location[]) => {
  const tree = [...locations]
  let found = false

  const traverse = function (current: Location[]) {
    forEach(current, (item, key) => {
      if (!found) {
        if (item.id === id) {
          item.expanded = !item.expanded
          found = true
        }

        if (item.children?.length) {
          traverse(item.children)
        }
      }
    })
  }

  traverse(tree)

  return tree
}

const updateTree = (nodes: Location[], params: Location, action: string) => {
  let newTree = cloneDeep(nodes)
  const patch =
    params && typeof params === 'object'
      ? normalizeLocationFromApi(params as unknown as Record<string, unknown>)
      : params

  switch (action) {
    case 'add':
      newTree = addLocation(nodes, patch)
      break
    case 'delete':
      newTree = deleteLocation(nodes, patch)
      break
    case 'edit':
      newTree = editLocation(nodes, patch)
      break
    default:
      return newTree
  }

  return newTree
}

const updateTreeBulk = (nodes: Location[], params: Location[]) => {
  return bulkAddLocations(nodes, params)
}

const addLocation = (nodes: Location[], location: Location) => {
  const { parentId } = location
  const nodeList = [...nodes]

  let isLocAdded = false

  if (!parentId) {
    const value = {
      ...normalizeLocationFromApi(
        location as unknown as Record<string, unknown>
      ),
      children: [],
      hasChild: false,
    }
    const index = sortedIndexBy(nodeList, value, 'name')

    nodeList.splice(index, 0, value)
  } else {
    const normalized = normalizeLocationFromApi(
      location as unknown as Record<string, unknown>
    )
    const traverse = function (current: Location[]) {
      forEach(current, (item) => {
        if (item.id === parentId) {
          isLocAdded = true

          item.hasChild = true

          if (item.children) {
            const index = sortedIndexBy(item.children, normalized, 'name')

            item.children.splice(index, 0, normalized)
          } else {
            item.children = [normalized]
          }
        } else if (item.hasChild && !isLocAdded) {
          traverse(item.children)
        }
      })
    }

    traverse(nodeList)
  }

  return nodeList
}

const bulkAddLocations = (nodes: Location[], locations: Location[]) => {
  let nodeList = [...nodes]

  let isLocAdded = false

  if (!locations[0].parentId) {
    const childs = map(locations, (item) => {
      return {
        ...normalizeLocationFromApi(item as unknown as Record<string, unknown>),
        children: [],
        hasChild: false,
      }
    })

    const updatedList = [...nodeList, ...childs]
    nodeList = sortBy(updatedList, ['name'])
  } else {
    const traverse = function (current: Location[]) {
      forEach(current, (item) => {
        if (item.id === locations[0].parentId) {
          const childs = map(locations, (loc) => {
            isLocAdded = true

            return {
              ...normalizeLocationFromApi(
                loc as unknown as Record<string, unknown>
              ),
              children: [],
              hasChild: false,
            }
          })

          item.hasChild = true

          if (item.children) {
            const updatedList = [...item.children, ...childs]

            item.children = sortBy(updatedList, ['name'])
          } else {
            item.children = sortBy(childs, ['name'])
          }
        } else if (item.hasChild && !isLocAdded) {
          traverse(item.children)
        }
      })
    }

    traverse(nodeList)
  }

  return nodeList
}

const deleteLocation = (nodes: Location[], params: Location) => {
  const { id, parentId } = params
  const nodeList = [...nodes]
  let isLocDeleted = false

  const traverse = function (current: Location[]) {
    forEach(current, (item, key) => {
      if (!isLocDeleted) {
        if (parentId) {
          // delete child location
          if (+item.id === +parentId) {
            isLocDeleted = true

            item.children = filter(item.children, (child) => {
              return child.id !== id
            })

            if (!item.children.length) {
              item.hasChild = false
            }
          } else if (item.hasChild && !isLocDeleted) {
            traverse(item.children)
          }
        } else {
          // delete property
          if (+item.id === +id) {
            isLocDeleted = true
            nodeList.splice(key, 1)
          }
        }
      }
    })
  }

  traverse(nodeList)

  return nodeList
}

const editLocation = (nodes: Location[], location: Location) => {
  const { id } = location
  const nodeList = [...nodes]
  let isLocEdited = false

  const traverse = function (current: Location[]) {
    forEach(current, (item, index) => {
      if (!isLocEdited) {
        if (item.id === id) {
          isLocEdited = true

          current.splice(index, 1)

          const neededIndex = sortedIndexBy(current, location, 'name')
          current.splice(neededIndex, 0, location)
        } else if (item.hasChild) {
          traverse(item.children)
        }
      }
    })
  }

  traverse(nodeList)

  return nodeList
}

export {
  changeExpanded,
  shrinkNode,
  prepareProperties,
  updateTree,
  updateTreeBulk,
}
